'use client';

import { type ReactNode } from 'react';

import { useSelectedLayoutSegment } from 'next/navigation';

import { cn } from '@ps/ui/utils';

/**
 * Master/detail flex shell for the community-channels page —
 * mirrors `MessagingPanes` so the two creator-dashboard list/detail
 * modules behave the same.
 *
 * Desktop (lg+): master pinned at 360px on the left with a vertical
 * separator, detail flexes the remainder.
 *
 * Mobile: whichever pane matches the route is visible. The master
 * shows at the index; once the user enters a channel
 * (`…/community/channels/[slug]`), the master is hidden behind the
 * detail.
 *
 * "Channel open" comes from {@link useSelectedLayoutSegment} (the active child
 * segment of the community-channels layout), NOT `usePathname()`. When a
 * soft-nav intercept opens over the page (the `/checkout` paywall launched from
 * a channel/thread message), the live pathname becomes `/checkout` and would
 * flip mobile back to the master — hiding the channel/thread behind the overlay.
 * The preserved background route's segment stays the slug, so the detail pane
 * remains mounted underneath.
 */
export function CommunityChannelsPanes({
  master,
  detail,
}: {
  master: ReactNode;
  detail: ReactNode;
}) {
  const isChannelOpen = useSelectedLayoutSegment() != null;

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div
        className={cn(
          // Light `--line` is #EEEEEE on the cream canvas — 1.02:1, invisible (QA).
          // `line-strong/80` is 1.31:1. The `visitor` pin keeps the PUBLIC community
          // (`(bio)/[url_code]/community/*`, wrapped in `ThemeScope theme="visitor"`)
          // on its current `line`; these render in-tree under that scope, so the
          // selector reaches them. Same treatment as the messaging panes.
          'lg:border-line-strong/80 lg:[[data-theme=visitor]_&]:border-line flex min-h-0 min-w-0 flex-col lg:w-[360px] lg:flex-none lg:border-r',
          isChannelOpen ? 'hidden lg:flex' : 'flex flex-1',
        )}
      >
        {master}
      </div>
      <div
        className={cn(
          'min-h-0 min-w-0 flex-1 lg:flex',
          isChannelOpen ? 'flex' : 'hidden lg:flex',
        )}
      >
        {detail}
      </div>
    </div>
  );
}
